import { Form, Modal, Space, Spin, Typography, message } from "antd";
import { CloseOutlined, ExclamationCircleFilled, RightOutlined } from "@ant-design/icons";
import copyIcon from "../../assets/svg/copy.svg";
import { FileType, ORDER_STATUS_DICT, OrderStatus } from "@ifa/utils";

import { FilePreviewModal } from "../../components/FilePreviewModal";
import CustomButton from "../../components/CustomButton";
import styles from "../OrderForm.module.css";
import { HighPremiumOrderModal } from "./components/HighPremiumOrderModal";
import { RiskAssessmentGenericForm } from "./RiskAssessmentGenericForm";
import { RiskAssessmentZuuForm } from "./RiskAssessmentZuuForm";
import { SignaturePad } from "./SignaturePad";
import type { RiskAssessmentController } from "./useRiskAssessmentController";

export function RiskAssessmentPageShell({ controller }: { controller: RiskAssessmentController }) {
  const {
    hideHeader,
    isZuuMode,
    questionSectionProps,
    renderUploadBox,
    craAttachmentConfig,
    originalCraData,
    orderData,
    navigate,
    loading,
    handleBack,
    handleViewRiskResults,
    handleSubmitClick,
    form,
    onFinish,
    onFinishFailed,
    isReadOnly,
    isSignatureModalOpen,
    closeSignatureModal,
    signaturePadRef,
    handleSaveSignature,
    tempSignature,
    currentUser,
    clearSignature,
    handleConfirmSignature,
    isSignatureReady,
    setIsSignatureReady,
    isRiskModalOpen,
    closeRiskModal,
    loadingRiskResults,
    craResults,
    mapCraRiskResult,
    CRA_PROJECT_LABELS,
    isOrderModalOpen,
    cancelOrderModal,
    handleSaveOrder,
    editingOrder,
    companyList,
    previewOpen,
    closePreview,
    previewFile,
  } = controller;

  return (
    <div className={`${styles["order-form-container"]}`}>
      {!hideHeader && (
        <div className={styles["sticky-header-container"]}>
          <header className={styles["order-header"]}>
            <div className={styles["header-left"]}>
              <div className={styles["header-title"]}>
                <span className={styles["header-breadcrumb"]} onClick={() => navigate("/orders")} style={{ cursor: "pointer", color: "#666" }}>
                  訂單
                </span>
                <RightOutlined className={styles["header-separator"]} style={{ fontSize: "12px", margin: "0 8px" }} />
                <span>訂單紀錄/風險評估問卷</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {orderData && (
                <div className={styles["header-info"]}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                    <span className={`${styles["status-tag"]} ${styles[`status-${orderData.orderStatus || OrderStatus.Draft}`]}`}>
                      {ORDER_STATUS_DICT[orderData.orderStatus as OrderStatus] || "草稿"}
                    </span>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span className={styles["info-item"]} style={{ fontSize: 12 }}>
                        <span className={styles["info-label"]}>編號:</span> {orderData.orderNo || "-"}
                      </span>
                      {orderData.orderNo && (
                        <img
                          src={copyIcon}
                          alt="copy"
                          style={{ marginLeft: 4, cursor: "pointer", width: 12, height: 12 }}
                          onClick={() => {
                            navigator.clipboard.writeText(orderData.orderNo || "");
                            message.success("已複製");
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{ width: 1, height: 32, background: "#E8E8E8", margin: "0 24px" }} />
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <span className={styles["info-item"]} style={{ fontSize: 12, lineHeight: "14px", color: "#999" }}>
                      投保人 / 受保人 :
                    </span>
                    <span className={styles["info-item"]} style={{ fontSize: 14, lineHeight: "20px", fontWeight: 500, color: "#333" }}>
                      {(orderData as any).policyholder?.basic?.chineseSurname || (orderData as any).policyholder?.name || ""}
                      {(orderData as any).policyholder?.basic?.chineseGivenName || ""}
                      <span style={{ color: "#E8E8E8", margin: "0 8px" }}>/</span>
                      {(orderData as any).insured?.basic?.chineseSurname || (orderData as any).insured?.name || ""}
                      {(orderData as any).insured?.basic?.chineseGivenName || ""}
                    </span>
                  </div>
                  <div style={{ width: 1, height: 32, background: "#E8E8E8", marginLeft: 24 }} />
                </div>
              )}
              <div className={styles["header-close"]} onClick={() => navigate("/orders")} style={{ cursor: "pointer", marginLeft: 16 }}>
                <CloseOutlined style={{ fontSize: 18, color: "#999" }} />
              </div>
            </div>
          </header>
        </div>
      )}

      <div style={{ padding: "24px", margin: "0 auto", width: "100%", overflowY: "auto" }}>
        <div className="flex justify-center items-center mb-6">
          <Typography.Title level={3} style={{ margin: 0 }}>
            客戶風險評估表
          </Typography.Title>
        </div>

        {originalCraData?.auditStatus && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 16px",
                borderRadius: 6,
                fontWeight: 500,
                fontSize: 14,
                background: originalCraData.auditStatus === "APPROVED" ? "#f6ffed" : "#fff2f0",
                border: `1px solid ${originalCraData.auditStatus === "APPROVED" ? "#b7eb8f" : "#ffccc7"}`,
                color: originalCraData.auditStatus === "APPROVED" ? "#52c41a" : "#ff4d4f",
              }}
            >
              CRA審核結果：{originalCraData.auditStatus === "APPROVED" ? "審核通過" : "審核不通過"}
            </div>
            {originalCraData.auditStatus === "REJECTED" && originalCraData.auditReason && (
              <div
                style={{
                  marginTop: 8,
                  padding: "10px 16px",
                  background: "#fff2f0",
                  border: "1px solid #ffccc7",
                  borderRadius: 6,
                  color: "#ff4d4f",
                  fontSize: 14,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                不通過原因：{originalCraData.auditReason}
              </div>
            )}
          </div>
        )}

        <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed} scrollToFirstError>
          {isZuuMode ? <RiskAssessmentZuuForm {...questionSectionProps} /> : <RiskAssessmentGenericForm {...questionSectionProps} />}

          <Form.Item noStyle shouldUpdate={(prev, current) => prev.exceedsHighPremiumThreshold !== current.exceedsHighPremiumThreshold}>
            {({ getFieldValue }) =>
              getFieldValue("exceedsHighPremiumThreshold") === "A" && craAttachmentConfig.length > 0 ? (
                <div className="mb-4">
                  <style>{`
                    .custom-upload-wrapper .ant-upload-select-picture-card,
                    .custom-upload-wrapper .ant-upload-list-picture-card-container {
                      width: 160px !important;
                      height: 110px !important;
                      margin-bottom: 8px !important;
                    }
                    .custom-upload-wrapper .ant-upload-list-item-container {
                      width: auto !important;
                      height: auto !important;
                    }
                    .upload-item-container {
                      display: inline-flex;
                      flex-direction: column;
                      align-items: flex-start;
                      margin-right: 32px;
                      margin-bottom: 24px;
                      vertical-align: top;
                    }
                    .upload-label {
                      margin-top: 8px;
                      color: #666;
                      font-size: 14px;
                      max-width: 160px;
                      text-align: left;
                      line-height: 1.4;
                    }
                    .required-mark {
                      color: #ff4d4f;
                      margin-right: 4px;
                    }
                  `}</style>
                  <div className="custom-upload-wrapper" style={{ display: "flex", flexWrap: "wrap" }}>
                    {craAttachmentConfig.map((config) =>
                      config.type ? (
                        <div key={config.type}>
                          {renderUploadBox(config.type as FileType, config.name || config.type, config.required || false)}
                        </div>
                      ) : null,
                    )}
                  </div>
                </div>
              ) : null
            }
          </Form.Item>

          <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg flex justify-between items-center z-50">
            <CustomButton height={35} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={handleBack} disabled={loading}>
              返回上一頁
            </CustomButton>
            <Space>
              <CustomButton height={35} bgColor="var(--theme-primary)" color="white" onClick={handleViewRiskResults}>
                查看四項評估結果
              </CustomButton>
              {!isReadOnly && (
                <CustomButton height={35} bgColor="var(--theme-primary)" color="white" loading={loading} onClick={handleSubmitClick}>
                  提交審核
                </CustomButton>
              )}
            </Space>
          </div>
          <div style={{ height: 80 }} />

          <Modal title="業務代表簽名" open={isSignatureModalOpen} onCancel={closeSignatureModal} width={800} centered destroyOnClose footer={null}>
            <div
              style={{
                padding: 16,
                background: "#FFFBE6",
                borderBottom: "1px solid #FFE58F",
                color: "#FAAD14",
                textAlign: "center",
              }}
            >
              <ExclamationCircleFilled /> 請使用正楷簽名
            </div>
            <div
              style={{
                padding: 24,
                display: "flex",
                justifyContent: "center",
                background: "#f0f2f5",
              }}
            >
              <div style={{ background: "#fff", border: "1px solid #e8e8e8" }}>
                <SignaturePad
                  ref={signaturePadRef}
                  width={700}
                  height={300}
                  onSave={handleSaveSignature}
                  onContentChange={setIsSignatureReady}
                  showControls={false}
                  defaultValue={tempSignature}
                  backgroundName={currentUser?.name || ""}
                />
              </div>
            </div>
            <div
              style={{
                padding: "16px 24px",
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <CustomButton height={40} fontSize={16} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={closeSignatureModal}>
                取消
              </CustomButton>
              <Space>
                <CustomButton height={40} fontSize={16} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} onClick={clearSignature}>
                  重寫
                </CustomButton>
                <CustomButton height={40} fontSize={16} bgColor="var(--theme-primary)" color="#fff" disabled={!isSignatureReady} onClick={handleConfirmSignature}>
                  確認並提交
                </CustomButton>
              </Space>
            </div>
          </Modal>

          <Modal title="風險評估結果" open={isRiskModalOpen} onCancel={closeRiskModal} width={800} footer={null}>
            {loadingRiskResults ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <Spin tip="載入風險評估結果中..." />
              </div>
            ) : (
              <div style={{ padding: 24 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e8e8e8" }}>
                      <th style={{ padding: 12, textAlign: "left" }}>評估項目</th>
                      <th style={{ padding: 12, textAlign: "left" }}>評估結果</th>
                      <th style={{ padding: 12, textAlign: "left" }}>原因</th>
                    </tr>
                  </thead>
                  <tbody>
                    {craResults.map((cra, index) => (
                      <tr key={cra.project || `cra-${index}`} style={{ borderBottom: "1px solid #e8e8e8" }}>
                        <td style={{ padding: 12 }}>
                          {cra.project ? (CRA_PROJECT_LABELS[cra.project] ?? cra.project) : "未知項目"}
                        </td>
                        <td style={{ padding: 12 }}>{mapCraRiskResult(cra.result)}</td>
                        <td style={{ padding: 12 }}>{cra.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Modal>

          <HighPremiumOrderModal
            visible={isOrderModalOpen}
            onCancel={cancelOrderModal}
            onSave={handleSaveOrder}
            initialValues={editingOrder}
            companyList={companyList}
            title={editingOrder ? "編輯外部保單" : "新增外部保單"}
          />
          <FilePreviewModal open={previewOpen} onClose={closePreview} fileUrl={previewFile?.url} fileName={previewFile?.name} />
        </Form>
      </div>
    </div>
  );
}
